import type { ReactNode } from 'react'
import type { ChallengeService } from '@duck/api-definitions'
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@duck/ui'
import { useTar, type TranslationKey } from '@/modules/i18n'
import { useFormatDuration } from './useFormatDuration'
import { ScoreRing } from '@/components/FeedbackDialog/ScoreRing'
import { BAND_CLASS, SEVERITY_COLOR } from './severity'

type StatKey = 'activeMinutes' | 'sessions' | 'pastedShare' | 'typedChars' | 'runs' | 'saves' | 'externalInserts' | 'internalInserts' | 'longestAway' | 'idleMinutes'

const Hint = ({ tip, children, className }: { tip: string; children: ReactNode; className?: string }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className={cn('cursor-help underline decoration-dotted decoration-muted-foreground/60 underline-offset-4', className)}>{children}</span>
    </TooltipTrigger>
    <TooltipContent className="max-w-[260px] text-xs">{tip}</TooltipContent>
  </Tooltip>
)

export const ActivitySummary = ({ report, sessionCount, onJumpToRule }: { report: ChallengeService.ActivityReport; sessionCount: number; onJumpToRule: (rule: string) => void }) => {
  const { t } = useTar()
  const formatDuration = useFormatDuration()
  const s = report.summary
  const stats: { key: StatKey; value: string }[] = [
    { key: 'activeMinutes', value: t('adminActivityReport.ledger.activeMinutes', { n: s.activeMinutes }) },
    { key: 'sessions', value: String(sessionCount) },
    { key: 'pastedShare', value: `${Math.round(s.pastedShare * 100)}%` },
    { key: 'typedChars', value: String(s.typedChars) },
    { key: 'runs', value: s.runErrors > 0 ? t('adminActivityReport.stats.runsWithErrors', { runs: s.runs, errors: s.runErrors }) : String(s.runs) },
    { key: 'saves', value: String(s.saves) },
    { key: 'externalInserts', value: String(s.externalInserts) },
    { key: 'internalInserts', value: String(s.internalInserts) },
    { key: 'longestAway', value: formatDuration(s.longestAwayMs) },
    { key: 'idleMinutes', value: String(s.idleMinutes) },
  ]

  return (
    <TooltipProvider delayDuration={200}>
      <aside className="space-y-5">
        <div className="flex flex-col items-center gap-2">
          <Hint tip={t('adminActivityReport.scoreTooltip')} className="no-underline">
            <ScoreRing grade={report.score} max={100} label={t('adminActivityReport.scoreLabel')} />
          </Hint>
          <span className={cn('rounded-full px-3 py-0.5 text-xs font-medium', BAND_CLASS[report.band])}>{t(`adminActivityReport.band.${report.band}`)}</span>
        </div>

        {s.clientTelemetryReceived === false && (
          <p className="rounded bg-warning/15 px-2 py-1 text-xs text-warning">{t('adminActivityReport.noClientTelemetry')}</p>
        )}

        <div className="text-[13px]">
          <div className="flex items-center justify-between py-2 text-muted-foreground">
            <span>{t('adminActivityReport.scoreStart')}</span>
            <span className="tabular-nums" dir="ltr">100</span>
          </div>
          {report.reasons.length === 0 ? (
            <p className="border-y border-border py-2.5 text-muted-foreground">{t('adminActivityReport.noReasons')}</p>
          ) : (
            <ul className="m-0 list-none p-0">
              {report.reasons.map((r) => (
                <li key={r.rule} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2.5 border-t border-border py-2.5 last:border-b">
                  <span>
                    <span className="me-1.5 inline-block h-2 w-2 rounded-full align-middle" style={{ background: SEVERITY_COLOR[r.label] }} />
                    {/* unknown rules render their raw key by design */}
                    {t(`adminActivityReport.rules.${r.rule}` as TranslationKey)}
                    {r.count > 1 ? <span className="ms-1 text-muted-foreground">{t('adminActivityReport.count', { count: r.count })}</span> : null}
                  </span>
                  <span className="row-span-2 self-center text-[15px] tabular-nums text-error" dir="ltr">{t('adminActivityReport.penalty', { points: r.penalty })}</span>
                  <small className="text-muted-foreground">
                    <Hint tip={t(`adminActivityReport.ruleHints.${r.rule}` as TranslationKey)}>{t(`adminActivityReport.severity.${r.label}`)}</Hint>
                    {' · '}
                    <button type="button" className="text-primary hover:underline" onClick={() => onJumpToRule(r.rule)}>{t('adminActivityReport.jumpTo')}</button>
                  </small>
                </li>
              ))}
            </ul>
          )}
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-[13px] text-muted-foreground">
          {stats.map((st) => (
            <div key={st.key}>
              <dt><Hint tip={t(`adminActivityReport.statHints.${st.key}`)}>{t(`adminActivityReport.stats.${st.key}`)}</Hint></dt>
              <dd className="m-0 text-lg font-medium tabular-nums text-foreground" dir="ltr">{st.value}</dd>
            </div>
          ))}
        </dl>
      </aside>
    </TooltipProvider>
  )
}
